/**
 * Data state constants for TimechainStats
 *
 * Used to represent the current state of data across the application.
 * Helps provide consistent error handling and user feedback.
 */

/**
 * Possible states for data values
 */
export const DATA_STATES = {
  /** Data is currently being loaded */
  LOADING: 'loading',

  /** Data loaded successfully and is valid */
  SUCCESS: 'success',

  /** Data failed to load (API error, network error, etc.) */
  ERROR: 'error',

  /** Data loaded but is older than threshold (>10 minutes) */
  STALE: 'stale',

  /** Data is calculated from fallback source (not primary API) */
  FALLBACK: 'fallback',

  /** Data validation failed (structure mismatch) */
  INVALID: 'invalid'
};

/**
 * User-friendly error messages for different states
 */
export const ERROR_MESSAGES = {
  NO_DATA: 'Data unavailable',
  STALE_DATA: 'Using cached data',
  API_ERROR: 'API error',
  NETWORK_ERROR: 'Network error',
  TIMEOUT_ERROR: 'Request timeout',
  VALIDATION_ERROR: 'Invalid data',
  CALCULATION_ERROR: 'Unable to calculate'
};

/**
 * Thresholds for staleness detection (in milliseconds)
 */
export const STALENESS_THRESHOLDS = {
  /** Data older than 10 minutes is critical */
  CRITICAL: 10 * 60 * 1000,

  /** Data older than 7 minutes gets a warning */
  WARNING: 7 * 60 * 1000,

  /** Data older than 5 minutes is considered fresh */
  FRESH: 5 * 60 * 1000
};

/**
 * Get data state based on metadata
 * @param {Object} metadata - Endpoint metadata from API client
 * @returns {string} One of DATA_STATES
 */
export const getDataState = (metadata) => {
  if (!metadata) return DATA_STATES.LOADING;

  // Check explicit status
  if (metadata.status === 'error') return DATA_STATES.ERROR;
  if (metadata.status === 'invalid') return DATA_STATES.INVALID;

  // Check staleness
  if (metadata.timestamp) {
    const age = Date.now() - metadata.timestamp;
    if (age > STALENESS_THRESHOLDS.CRITICAL) {
      return DATA_STATES.STALE;
    }
  }

  // Default to success if status is success
  if (metadata.status === 'success') return DATA_STATES.SUCCESS;

  return DATA_STATES.LOADING;
};

/**
 * Get appropriate error message for a data state
 * @param {string} state - One of DATA_STATES
 * @param {Object} metadata - Optional metadata for context
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (state, metadata) => {
  switch (state) {
    case DATA_STATES.ERROR:
      if (metadata?.errorType === 'timeout') {
        return ERROR_MESSAGES.TIMEOUT_ERROR;
      }
      if (metadata?.errorType?.startsWith('http_')) {
        return ERROR_MESSAGES.API_ERROR;
      }
      return ERROR_MESSAGES.NETWORK_ERROR;

    case DATA_STATES.STALE:
      return ERROR_MESSAGES.STALE_DATA;

    case DATA_STATES.INVALID:
      return ERROR_MESSAGES.VALIDATION_ERROR;

    case DATA_STATES.LOADING:
      return 'Loading...';

    default:
      return ERROR_MESSAGES.NO_DATA;
  }
};
